/**
 * state/WidgetContext.jsx
 * Глобальный контекст виджета.
 * Содержит: состояние, dispatch, вспомогательные экшн-криейторы.
 */
import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { widgetReducer, initialState } from './reducers';
import { ACTIONS, SCREENS, STORAGE_KEYS } from '@/utils/constants';
import { storageService } from '@/services/storage';
import { fetchChatUserProfile, mapChatUserToLeadData } from '@/services/liveChat';

const WidgetContext = createContext(null);

export function WidgetProvider({ children }) {
  const [state, dispatch] = useReducer(widgetReducer, initialState);

  // Инициализация: восстанавливаем язык, UUID и контактные данные с бэкенда
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const savedLang = storageService.get(STORAGE_KEYS.LANGUAGE);
      const savedUUID = storageService.get(STORAGE_KEYS.USER_ID);

      const userId   = savedUUID || uuidv4();
      const language = savedLang || 'en';

      if (!savedUUID) {
        storageService.set(STORAGE_KEYS.USER_ID, userId);
      }

      dispatch({ type: ACTIONS.SET_USER_FIELD, payload: { field: 'id',        value: userId } });
      dispatch({ type: ACTIONS.SET_USER_FIELD, payload: { field: 'createdAt', value: new Date().toISOString() } });
      dispatch({ type: ACTIONS.SET_USER_FIELD, payload: { field: 'lastActivity', value: new Date().toISOString() } });

      if (savedLang) {
        // Язык уже выбирался ранее — пропускаем экран выбора
        dispatch({ type: ACTIONS.SET_LANGUAGE, payload: language });
        dispatch({ type: ACTIONS.SET_SCREEN,   payload: SCREENS.WELCOME });
      }

      const profile = await fetchChatUserProfile(userId);
      if (cancelled) return;

      const leadData = mapChatUserToLeadData(profile);
      if (leadData) {
        dispatch({ type: ACTIONS.SET_LEAD_DATA, payload: leadData });
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  // Обновляем lastActivity при каждом взаимодействии
  useEffect(() => {
    dispatch({
      type: ACTIONS.SET_USER_FIELD,
      payload: { field: 'lastActivity', value: new Date().toISOString() },
    });
  }, [state.screen]);

  /* ---- Вспомогательные action creators ---- */

  const toggleOpen = useCallback(() => {
    dispatch({ type: ACTIONS.TOGGLE_OPEN });
  }, []);

  const setOpen = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_OPEN, payload: value });
  }, []);

  const goTo = useCallback((screen) => {
    dispatch({ type: ACTIONS.SET_SCREEN, payload: screen });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: ACTIONS.GO_BACK });
  }, []);

  const goToMenu = useCallback(() => {
    dispatch({ type: ACTIONS.GO_MENU });
  }, []);

  const setLanguage = useCallback((lang) => {
    storageService.set(STORAGE_KEYS.LANGUAGE, lang);
    dispatch({ type: ACTIONS.SET_LANGUAGE, payload: lang });
  }, []);

  const setInterest = useCallback((field, value) => {
    dispatch({ type: ACTIONS.SET_INTERESTS, payload: { field, value } });
  }, []);

  const setLeadField = useCallback((field, value) => {
    dispatch({ type: ACTIONS.SET_LEAD_FIELD, payload: { field, value } });
  }, []);

  const setChannel = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_USER_FIELD, payload: { field: 'channel', value } });
  }, []);

  const setSelectedEvent = useCallback((eventId) => {
    dispatch({ type: ACTIONS.SET_SELECTED_EVENT, payload: eventId });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: ACTIONS.RESET });
  }, []);

  const value = {
    state,
    dispatch,
    // action creators
    toggleOpen,
    setOpen,
    goTo,
    goBack,
    goToMenu,
    setLanguage,
    setInterest,
    setLeadField,
    setChannel,
    setSelectedEvent,
    reset,
  };

  return (
    <WidgetContext.Provider value={value}>
      {children}
    </WidgetContext.Provider>
  );
}

/** Хук доступа к контексту */
export function useWidget() {
  const ctx = useContext(WidgetContext);
  if (!ctx) throw new Error('useWidget must be used within WidgetProvider');
  return ctx;
}
